import React, { useEffect, useState } from "react";
import "../../styles/settings.css";
import toast from "react-hot-toast";
import SettingServices from "../../services/SettingServices";
import SettingsTabs, { TABS } from "./componests/SettingsTabs";
import GeneralSettings from "./componests/GeneralSettings";
import GamesSettings from "./componests/GamesSettings";
import WalletSettings from "./componests/WalletSettings";
import NotesSettings from "./componests/NotesSettings";
import SettingsHistory from "./componests/SettingsHistory";

const VALID_TABS = TABS.map((t) => t.key);

/** Reads ?tab= so a settings link can point straight at one section. */
function initialTab() {
  try {
    const requested = new URLSearchParams(window.location.search).get("tab");
    return VALID_TABS.includes(requested) ? requested : "general";
  } catch (_) {
    return "general";
  }
}

/**
 * Shell for the Settings page. Owns the `settings` row - the branding fields,
 * the amounts and the game toggles all live on it and the update endpoint
 * writes every column at once, so both tabs that touch it save through the one
 * handler here. Everything else is self-contained in its own tab component.
 */
export default function Settings() {
  const settingApi = new SettingServices();

  const [activeTab, setActiveTab] = useState(initialTab);

  const [settingData, setSettingData] = useState(null);
  const [mainTitle, setMainTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [yantra, setYantra] = useState(0);
  const [winning, setWinning] = useState(0);
  const [difference, setDifference] = useState(0);
  const [games, setGames] = useState({ "3min": false, "5min": false });

  const [loading, setLoading] = useState(false);
  // Bumped after any save so the History tab refetches when it is next opened.
  const [historyKey, setHistoryKey] = useState(0);

  const changeTab = (key) => {
    setActiveTab(key);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", key);
      window.history.replaceState({}, "", url);
    } catch (_) {
      /* non-fatal: the tab still switches */
    }
  };

  const getSettingData = () => {
    settingApi
      .getSettingData()
      .then((response) => {
        if (response.isSuccess) {
          const data = response.data;
          if (data) {
            setSettingData(data);
            setMainTitle(data?.main_title || "");
            setSubTitle(data?.sub_title || "");
            setYantra(data?.yantra_amount);
            setWinning(data?.winning_amount);
            setDifference(data?.difference);
            setGames({
              "3min": data?.game_3min,
              "5min": data?.game_5min,
            });
          }
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error get settings:", error);
        toast.dismiss();
        toast.error("Error get settings");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getSettingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update-setting-data rewrites every column of the settings row, so the
  // amounts and game flags have to go back with it even though nothing edits
  // them here any more. They are still what the live engine reads; the Games
  // tab manages the new `games` table instead. Sending the values loaded in
  // getSettingData keeps them untouched.
  const handleSave = () => {
    setLoading(true);
    settingApi
      .UpdateSettingData({
        main_title: mainTitle,
        sub_title: subTitle,
        yantra_amount: yantra,
        winning_amount: winning,
        difference: difference,
        game_3min: games["3min"],
        game_5min: games["5min"],
      })
      .then((response) => {
        if (response.isSuccess) {
          toast.dismiss();
          toast.success(response.message || "Settings updated successfully");
          try {
            window.dispatchEvent(
              new CustomEvent("app-brand-updated", {
                detail: { main_title: mainTitle, sub_title: subTitle },
              }),
            );
          } catch (_) {}
          getSettingData();
          setHistoryKey((k) => k + 1);
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error updating settings:", error);
        toast.dismiss();
        toast.error("Failed to save settings");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const panel = (key, node) => (
    <div
      role="tabpanel"
      id={`settings-panel-${key}`}
      aria-labelledby={`settings-tab-${key}`}
      hidden={activeTab !== key}
    >
      {activeTab === key ? node : null}
    </div>
  );

  return (
    <div className="settings-wrapper">
      <SettingsTabs active={activeTab} onChange={changeTab} />

      <div className={`settings-card settings-panel ${loading ? "blur" : ""}`}>
        {panel(
          "general",
          <GeneralSettings
            mainTitle={mainTitle}
            setMainTitle={setMainTitle}
            subTitle={subTitle}
            setSubTitle={setSubTitle}
            onSave={handleSave}
            saving={loading}
          />,
        )}

        {panel("games", <GamesSettings />)}

        {panel("wallet", <WalletSettings />)}

        {panel(
          "notes",
          <NotesSettings
            settingData={settingData}
            onSaved={() => {
              getSettingData();
              setHistoryKey((k) => k + 1);
            }}
          />,
        )}

        {panel("history", <SettingsHistory refreshKey={historyKey} />)}
      </div>
    </div>
  );
}
